/**
 * NewClientIntakeForm — SCRIPT-DRIVEN client intake for the Runner.
 * ─────────────────────────────────────────────────────────────────
 * Field order + question text come from the template's `prompts:` string,
 * parsed by parsePromptScript. Field metadata (input type, dropdown source,
 * required flag) comes from the Overwatch Field Registry at
 * /api/overwatch/fields?category=client. Add / reorder / remove fields
 * happens in the Admin Studio; this component never has a hardcoded list.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Loader2, Save, CheckCircle2, ExternalLink } from 'lucide-react';
import { InlineParamEdit, FieldRowControls, advanceOnEnter } from './scriptEditControls';
import NameTypeahead from './NameTypeahead';
import { toTitleCase } from '../../../lib/titleCase';

// v10.28 (Feb 2026 · Batch B · Issue 5a) — Which field keys should
// auto-title-case on blur. Address street/suburb/city + human names
// are the safe set for a Waikato roofing CRM. Emails, phones, notes,
// gate codes, postcodes, comments, URLs are deliberately excluded.
const _AUTO_TITLECASE_KEYS = new Set([
  'name_first',
  'name_last',
  'company_name',
  'address_street',
  'address_suburb',
  'address_city',
]);

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

/** Parse "field_key: prompt text" lines into an ordered array. */
export function parsePromptScript(promptsText) {
  if (!promptsText) return [];
  const out = [];
  promptsText.split('\n').forEach(line => {
    const m = line.match(/^\s*([a-z0-9_]+)\s*:\s*(.*)$/i);
    if (m) out.push({ key: m[1], prompt: (m[2] || '').trim() });
  });
  return out;
}

function useFieldRegistry(category) {
  const [byKey, setByKey] = useState({});
  useEffect(() => {
    let cancelled = false;
    api.get('/overwatch/fields', { params: { category } })
      .then(res => {
        if (cancelled) return;
        const map = {};
        (res.data?.fields || []).forEach(f => { map[f.key] = f; });
        setByKey(map);
      })
      .catch(() => setByKey({}));
    return () => { cancelled = true; };
  }, [category]);
  return byKey;
}

function useDropdown(category, cascadeValue) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (!category) return;
    let cancelled = false;
    const params = cascadeValue ? { parent: cascadeValue } : {};
    api.get(`/dropdowns/${category}`, { params })
      .then(res => {
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : (res.data?.options || res.data?.items || []);
        setOptions(raw.map(o => typeof o === 'string' ? { value: o, label: o } : { value: o.value || o.name || o.key, label: o.label || o.name || o.value || o.key }));
      })
      .catch(() => setOptions([]));
    return () => { cancelled = true; };
  }, [category, cascadeValue]);
  return { options };
}

function FieldInput({ field, value, onChange, form, testid }) {
  const cascadeVal = field.cascade_from ? form[field.cascade_from] : null;
  const { options } = useDropdown(field.dropdown, cascadeVal);

  if (field.type === 'multi-dropdown') {
    const selected = Array.isArray(value) ? value : (value ? String(value).split(',').map(s => s.trim()).filter(Boolean) : []);
    const toggle = (opt) => {
      const has = selected.includes(opt);
      onChange(has ? selected.filter(x => x !== opt) : [...selected, opt]);
    };
    return (
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map(o => {
          const isOn = selected.includes(o.value);
          return (
            <button key={o.value} type="button" onClick={() => toggle(o.value)}
              data-testid={`${testid}-${o.value}`}
              className={`px-3 py-1.5 text-sm rounded-full border transition
                ${isOn
                  ? 'border-sky-500 bg-sky-500 text-white shadow-sm'
                  : 'border-slate-300 bg-white text-slate-700 hover:border-sky-300 hover:bg-sky-50'}`}>
              {isOn && '✓ '}{o.label}
            </button>
          );
        })}
      </div>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
          data-testid={testid} className="w-4 h-4" />
        <span className="text-sm text-slate-600">Yes</span>
      </label>
    );
  }
  if (field.type === 'textarea') {
    return (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)}
        data-testid={testid} rows={2}
        className="w-full text-sm px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-300" />
    );
  }
  if (field.type === 'dropdown') {
    return (
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full text-sm px-3 py-2 border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-sky-300">
        <option value="">— choose —</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (field.type === 'dropdown-searchable') {
    return (
      <div>
        <input list={`dl-${field.key}`}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={cascadeVal ? `Filtered by ${cascadeVal}` : 'Type to filter…'}
          data-testid={testid}
          className="w-full text-sm px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-300" />
        <datalist id={`dl-${field.key}`}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </datalist>
      </div>
    );
  }
  if (field.type === 'typeahead') {
    return (
      <NameTypeahead
        value={value || ''}
        onChange={onChange}
        suggestionsSource={field.suggestions_source || field.key}
        placeholder={field.placeholder || 'Type to search or add new…'}
        testId={testid}
      />
    );
  }
  return (
    <input type={field.type} value={value || ''} onChange={e => onChange(e.target.value)}
      onBlur={e => {
        if (field.type === 'text' && _AUTO_TITLECASE_KEYS.has(field.key)) {
          const nice = toTitleCase(e.target.value || '');
          if (nice !== (e.target.value || '')) onChange(nice);
        }
      }}
      placeholder={field.placeholder || ''} data-testid={testid}
      className="w-full text-sm px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-300" />
  );
}

export default function NewClientIntakeForm({ initial, existingClientId, existingPropertyId, prompts, onSave, onCancel, autoSaveDraft, editCtx }) {
  const registry = useFieldRegistry('client');
  const script = useMemo(() => parsePromptScript(prompts), [prompts]);
  const [form, setForm] = useState(initial || {});
  const initialSnapshot = useMemo(() => JSON.stringify(initial || {}), [initial]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const autosaveTimer = useRef(null);
  const isUpdate = !!existingClientId;

  // Build the displayable field list by resolving each script line against
  // the registry. Fields not (yet) in the registry are skipped (with a warn).
  const fields = useMemo(() => {
    return script
      .map(({ key, prompt }) => {
        const meta = registry[key];
        if (!meta) return null;
        return { ...meta, prompt: prompt || meta.default_prompt || '' };
      })
      .filter(Boolean);
  }, [script, registry]);

  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      if (autoSaveDraft) { autoSaveDraft(form); setSavedAt(new Date()); }
    }, 3000);
    return () => clearTimeout(autosaveTimer.current);
  }, [form, autoSaveDraft]);

  // iter303aj-fix5 — Postcode auto-fill. When both suburb + city are set
  // AND the postcode field is empty, look up the best-known postcode for
  // that pair and silently populate. Operator can Tab past to confirm or
  // type over. Only fires when a real change happened (avoid infinite
  // loops); never overrides an operator-typed value.
  const postcodeLookupRef = useRef({ suburb: '', city: '' });
  useEffect(() => {
    const suburb = (form.address_suburb || '').trim();
    const city   = (form.address_city   || '').trim();
    const existingPc = (form.address_postcode || '').trim();
    if (!suburb || !city) return;
    if (existingPc) return;
    // Skip repeat lookups for the same (suburb, city) pair.
    if (postcodeLookupRef.current.suburb === suburb.toLowerCase()
     && postcodeLookupRef.current.city   === city.toLowerCase()) return;
    postcodeLookupRef.current = { suburb: suburb.toLowerCase(), city: city.toLowerCase() };
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/postcodes/lookup', { params: { suburb, city } });
        if (cancelled) return;
        if (data?.postcode) {
          setForm(f => f.address_postcode ? f : { ...f, address_postcode: data.postcode });
        }
      } catch { /* silent — operator can still type */ }
    })();
    return () => { cancelled = true; };
  }, [form.address_suburb, form.address_city, form.address_postcode]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!form.name_first || !form.name_last) { setError('First name and last name are required.'); return; }
    if (form.is_company && !form.company_name) { setError('Business name is required when "Is a Company" is ticked.'); return; }

    // v10.28 (Feb 2026 · Batch B · Issue 5a) — final auto-title-case
    // sweep before save. Catches typeahead / dropdown-searchable
    // paths where the field's own onBlur doesn't fire, plus operators
    // who paste an all-lowercase blob. Rule 1 (any uppercase → skip)
    // preserves McDonald / iPhone / DHL exactly as typed.
    let normalisedForm = form;
    const patch = {};
    _AUTO_TITLECASE_KEYS.forEach((k) => {
      const cur = form[k];
      if (typeof cur === 'string' && cur) {
        const nice = toTitleCase(cur);
        if (nice !== cur) patch[k] = nice;
      }
    });
    if (Object.keys(patch).length) {
      normalisedForm = { ...form, ...patch };
      setForm(normalisedForm);
    }

    setSaving(true);
    try {
      const noChanges = JSON.stringify(normalisedForm) === initialSnapshot;
      let clientRecord = null;
      let newId = existingClientId;
      if (isUpdate && noChanges) {
        clientRecord = { _id: existingClientId, ...normalisedForm };
      } else {
        const res = isUpdate
          ? await api.put(`/clients/${existingClientId}`, normalisedForm)
          : await api.post('/clients', normalisedForm);
        clientRecord = res.data;
        newId = res.data?._id || res.data?.id || existingClientId;
      }

      // Auto-create property at client's address, mirroring "Use Client Address" pattern.
      let propertyRecord = null;
      let propId = existingPropertyId || null;
      if (!existingPropertyId) {
        try {
          const propBody = {
            client_id: newId,
            property_address_num:    normalisedForm.address_num    || '',
            property_address_street: normalisedForm.address_street || '',
            property_address_suburb: normalisedForm.address_suburb || '',
            property_address_city:   normalisedForm.address_city   || '',
            property_comments: 'Auto-created from client intake — same address as client',
          };
          const propRes = await api.post('/properties', propBody);
          propertyRecord = propRes.data;
          propId = propRes.data?._id || propRes.data?.id;
          // Iter303ag-fix2 — Also proactively create a "Main Roof"
          // roof_details record so subsequent intake actions can UPDATE
          // instead of race-creating a new record, AND the property page
          // shows the roof card immediately.
          if (propId) {
            try {
              await api.post('/roof-details', {
                property_id: propId,
                description: 'Main Roof',
              });
            } catch (roofErr) {
              // Non-fatal — the Property Scripted Intake will create one
              // lazily if this fails.
              console.warn('Auto-create roof_details failed:', roofErr);
            }
          }
        } catch (propErr) {
          console.warn('Auto-create property failed:', propErr);
        }
      }

      onSave({
        client_id: newId,
        client_record: clientRecord,
        property_id: propId,
        property_record: propertyRecord,
        form: normalisedForm,
        no_changes: isUpdate && noChanges,
      });
    } catch (e) {
      // Pydantic 422 returns an ARRAY on `detail`; naively rendering it
      // crashes React with "Objects are not valid as a React child".
      const detail = e?.response?.data?.detail;
      let msg = e?.message || 'Save failed';
      if (Array.isArray(detail)) {
        msg = detail.map(d => {
          const loc = Array.isArray(d?.loc) ? d.loc.filter(x => x !== 'body').join('.') : '';
          return loc ? `${loc}: ${d?.msg || 'invalid'}` : (d?.msg || String(d));
        }).join('\n');
      } else if (typeof detail === 'string') { msg = detail; }
      else if (detail && typeof detail === 'object') { msg = JSON.stringify(detail); }
      setError(msg);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} onKeyDown={advanceOnEnter} className="space-y-5">
      <div className="bg-gradient-to-br from-sky-50 to-white border-l-4 border-sky-500 rounded-r-lg p-4 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-sky-700 font-semibold mb-1 flex items-center gap-2">
            <span>{isUpdate ? '✎' : '📝'}</span>
            <InlineParamEdit
              editCtx={editCtx}
              paramKey="header_title"
              value={editCtx?.headerTitle || (isUpdate ? 'Updating Existing Client' : 'New Client Intake')}
              placeholder="Header title"
              className=""
              dataTestidPrefix="intake-header-title"
              alwaysShowPencil
            />
          </div>
          <div className="text-slate-700 text-sm">
            <InlineParamEdit
              editCtx={editCtx}
              paramKey="header_description"
              value={editCtx?.headerDescription || 'Walk through the fields — read the prompt to the caller, then capture their answer. Auto-saved every few seconds.'}
              placeholder="Description script"
              className=""
              dataTestidPrefix="intake-header-description"
              multiline
              alwaysShowPencil
            />
          </div>
        </div>
        {isUpdate && existingClientId && (
          <a href={`/clients/${existingClientId}`} target="_blank" rel="noopener noreferrer"
            data-testid="intake-open-client-btn"
            className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-sky-700 bg-white border border-sky-300 rounded hover:bg-sky-50">
            <ExternalLink size={12} /> Open Client Details
          </a>
        )}
      </div>

      {fields.length === 0 && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          No fields in this intake yet. Edit the workflow template&apos;s <code>prompts:</code> list to add fields.
        </div>
      )}

      {fields.map((f, idx) => {
        // show_if supports `field` (show when truthy) and `!field` (show
        // when falsy) — chained via multiple show_if lines works because
        // parent gates hide their children automatically.
        if (f.show_if) {
          const inv = f.show_if.startsWith('!');
          const key = inv ? f.show_if.slice(1) : f.show_if;
          const on = !!form[key];
          if (inv ? on : !on) return null;
        }
        return (
          <div key={f.key} className="group border-l-2 border-slate-100 pl-4 relative">
            {f.prompt && (
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="text-sm text-sky-700 italic" data-testid={`intake-prompt-${f.key}`}>
                  💬 &quot;{f.prompt}&quot;
                </div>
                <FieldRowControls
                  editCtx={editCtx}
                  items={script}
                  idx={idx}
                  currentPrompt={f.prompt}
                />
              </div>
            )}
            <label className="block text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
              {f.required ? '🔒 ' : ''}{f.label}{f.required ? ' *' : ''}
            </label>
            <FieldInput field={f} value={form[f.key]} onChange={v => setField(f.key, v)}
              form={form} testid={`intake-input-${f.key}`} />
          </div>
        );
      })}

      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3 whitespace-pre-wrap">{error}</div>}

      <div className="flex items-center gap-3 sticky bottom-0 bg-white py-3 border-t border-slate-100">
        <div className="text-xs text-slate-400">
          {savedAt && (
            <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" /> Draft auto-saved {Math.max(1, Math.round((Date.now() - savedAt) / 1000))}s ago</span>
          )}
        </div>
        <span className="flex-1" />
        <button type="button" onClick={onCancel} data-testid="intake-cancel-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded">
          Cancel
        </button>
        <div className="flex flex-col items-end">
          <button type="submit" disabled={saving} data-testid="intake-save-btn"
            className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40 flex items-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Continue
          </button>
          <span className="text-[10px] text-slate-400 mt-1">saves &amp; updates the client record</span>
        </div>
      </div>
    </form>
  );
}
